import { LazyCategoryWell } from 'owa-categories';
import type { MailListItemFullThreeColumnProps } from './MailListItemComponentProps';
import {
    getConditionalFormattingStyles,
    ConditionalFormattingSurface,
} from 'owa-conditional-formatting';
import onCategoryClicked from '../utils/onCategoryClicked';
import renderReactions from '../utils/renderReactions';
import isValidEmailAddress from '../utils/isValidEmailAddress';
import classnames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import loc from 'owa-localize';
import {
    showPersonaCheckbox,
    onMailListItemClickHandler,
    useIsTypeRampUpdatesEnabled,
    useMailListItemContainerContext,
    getPreviewTextStyles,
} from 'owa-mail-list-item-shared';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import {
    full,
    medium,
    compact,
    reactionsContainer,
    containerForTwistyAndCheckboxSCL,
    containerPaddingCompact,
    containerPaddingNoCheckboxFull,
    containerPaddingNoCheckboxMedium,
    multiSelectNoTwistyDisplayed,
    secondRowThreeColumnUnread,
    secondRowThreeColumnUnreadTypeRamp,
    secondRowThreeColumn,
    secondRowThreeColumnPaddingNext,
    conversationHeaderContent,
    categoryContainerThreeColumn,
    categoryContainerThreeColumnFull,
    categoryContainerThreeColumnMediumOrCompact,
    sclContainer,
    firstColumnThreeColumn,
    firstColumnThreeColumnNoTwisty,
    column,
    multiSelectNoCheckbox,
    secondRowThreeColumnRead,
    secondLineTextThreeColumn,
    categoryWellThreeColumn,
    previewDisplaySCL,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';
import {
    firstRowThreeColumnUnreadNext,
    firstRowThreeColumnNext,
} from 'owa-mail-listitem-styles/lib/scss/ListViewItemCommonStyles.scss';
import {
    previewDisplaySCLCategories,
    previewDisplaySCLCategoriesTwoLine,
    previewDisplaySCLCategoryWellFloat,
    threeColFolderTag,
    priorityIconAlignTop,
} from './MailListItemFullThreeColumn.scss';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import shouldShowUnstackedReadingPane from 'owa-mail-organization-settings/lib/utils/shouldShowUnstackedReadingPane';
import type { TableView, MailFolderTableQuery } from 'owa-mail-list-store';
import { isMailListConversationView, isNeedsReplyView } from 'owa-mail-list-store';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import { type SortColumn } from 'owa-mail-list-store/lib/store/schema/SortBy';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { NeedsReplyItemFeedback } from 'owa-mail-needs-reply';
import type SingleRecipientType from 'owa-service/lib/contract/SingleRecipientType';
import isSenderImageEnabled from 'owa-messagelist-layout-option/lib/utils/isSenderImageEnabled';
import React from 'react';
import {
    MailListItemFirstLineText,
    MailListItemIconBar,
    MailListItemLabelsContainer,
    MailListItemLineText,
    MailListItemRichPreviewWell,
    MailListItemTimestamp,
    MailListItemTwistyButton,
    MailListItemUnreadCount,
    PersonaCheckbox,
    FolderTag,
    MailListItemMeetingInfo,
    MailListItemPreviewDisplay,
    MailListItemMarker,
    MailListItemUnreadBar,
} from 'owa-mail-list-item-components';
import { calculateAttachmentPreviewWellViewState } from 'owa-mail-layout';
import { senderEmailNotFoundLabel } from './MailListItem.locstring.json';
import { SuggestedUnsubscribe } from 'owa-suggested-unsubscribe-peek';
import { getDensityModeCssClass, getDensityModeString, getIsDarkTheme } from 'owa-fabric-theme';
import { CopilotInboxPriorityIcon } from 'owa-mail-copilot-inbox-shared';

const defaultExport: React.FunctionComponent<MailListItemFullThreeColumnProps> = observer(
    function MailListItemFullThreeColumn(props: MailListItemFullThreeColumnProps): JSX.Element {
        const {
            canArchive,
            canDelete,
            canPin,
            categories,
            conversationId,
            copilotInboxScore,
            copilotPrioritizeOnDeliveryOn,
            effectiveMentioned,
            firstLineText,
            firstLineTooltipText,
            hasAttachment,
            hasSharepointLink,
            highlightTerms,
            importance,
            isComplete,
            isFirstLevelExpanded,
            isFlagged,
            isInArchiveFolder,
            isInNotesFolder,
            isInOutboxFolder,
            isInJunkFolder,
            isLoadingSecondLevelExpansion,
            isPinned,
            renderMinimum,
            isSearchTable,
            isSecondLevelExpanded,
            isSelected,
            isSnoozed,
            isTaggedForBigScreen,
            isUnauthenticatedSender,
            itemClassIcon,
            lastDeliveryTimestamp,
            lastSender,
            latestItemId,
            listViewType,
            mailboxGuids,
            parentFolderDisplayName,
            parentFolderId,
            returnTime,
            rowId,
            rowKey,
            secondLineText,
            secondLineTooltipText,
            shouldShowExternalLabel,
            shouldShowRSVP,
            shouldShowTwisty,
            shouldSuggestUnsubscribe,
            showAttachmentPreview,
            showDraftsIndicator,
            showFolderTag,
            showPreviewText,
            numMessagePreviewLines,
            smimeType,
            supportsFlagging,
            supportsHoverIcons,
            supportsMove,
            supportsPinning,
            tableViewId,
            thirdLineText,
            unreadCount,
            isReminderSet,
            isScheduledSend,
            isProactiveDraft,
            bulkCategory,
            isCfPreview,
            cfPreviewMods,
            cfPreviewBackgroundColorStyle,
            cfPreviewUnreadBarVars,
        } = props;

        const tableView: TableView = getListViewStore().tableViews.get(tableViewId) as TableView;
        const { conditionalFormattingModifications: contextCfModifications } =
            useMailListItemContainerContext();
        const isDarkTheme = getIsDarkTheme();
        const conditionalFormattingModifications = cfPreviewMods
            ? cfPreviewMods
            : contextCfModifications;

        const isTypeRampUpdatesEnabled = useIsTypeRampUpdatesEnabled();
        const shouldAllowMultiLinePreview = isGlobalFeatureEnabled('tri-mlMultiLinePreview');
        const mailTableQuery = tableView?.tableQuery as MailFolderTableQuery;
        const isCategorySort = mailTableQuery?.sortBy?.sortColumn === 14;
        const isPrioritySort = mailTableQuery?.sortBy?.sortColumn === 16;

        const densityModeClass = getDensityModeCssClass(full, medium, compact);
        // derived properties based on props/flights
        const isUnread = unreadCount > 0;
        const showCategories = categories?.length > 0 && !isCategorySort;
        const showCategoriesInline =
            shouldShowUnstackedReadingPane() && isFirstLevelExpanded && !isCategorySort;
        const notExpanded =
            !isFirstLevelExpanded && !isSecondLevelExpanded && !isLoadingSecondLevelExpansion;
        const showPreviews = !shouldShowUnstackedReadingPane() || notExpanded;
        const showMultiSelectButton = !isSearchTable;
        const senderImageEnabled = isSenderImageEnabled();

        const showCheckbox = showPersonaCheckbox(isSearchTable, tableView) && !isCfPreview;

        const mailboxInfo = props.mailboxInfo ?? getMailboxInfo(tableView);
        const isConversationView = isMailListConversationView(tableView);

        const unifiedInboxMailboxSmtpTooltip = isUnifiedInboxMailboxInfo(getMailboxInfo(tableView))
            ? mailboxInfo?.mailboxSmtpAddress
            : undefined;

        // Legacy labels container (categories + folder tags on a separate row) when multi-line is off
        const showLabelsContainer =
            !shouldAllowMultiLinePreview && !showPreviewText && (showCategories || showFolderTag);

        // To ensure that preview text is not displayed for Newest at the top/bottom setting with conversation expansion
        const showPreviewTextConditionally = showPreviewText && notExpanded;

        // Two-line preview: categories float inline with preview text, clamped to 2 lines
        const useTwoLinePreviewFlow =
            shouldAllowMultiLinePreview &&
            showPreviewTextConditionally &&
            showPreviews &&
            showCategories &&
            numMessagePreviewLines === 2;

        // Where categories render depends on multi-line flight and whether preview text is shown
        const showCategoriesOnSubjectRow =
            showCategories &&
            ((shouldAllowMultiLinePreview && !showPreviewText) || showCategoriesInline);
        const showCategoriesOnPreviewRow =
            showCategories &&
            ((shouldAllowMultiLinePreview && showPreviewText) || !showCategoriesInline);

        // Where reactions render: on the subject row (legacy) or the preview row (multi-line)
        const showReactionsOnSubjectRow = !shouldAllowMultiLinePreview && !showPreviewText;

        const personaCheckboxComponent = (
            <PersonaCheckbox
                tableViewId={tableViewId}
                isSelected={isSelected}
                lastSender={lastSender}
                rowKey={rowKey}
                lastDeliveryTimestamp={lastDeliveryTimestamp}
                isUnauthenticatedSender={isUnauthenticatedSender}
                isCFPreview={isCfPreview}
            />
        );

        const conditionalFormattingTextStyle = React.useMemo(() => {
            if (
                isGlobalFeatureEnabled('tri-conditional-formatting-v2') &&
                conditionalFormattingModifications &&
                conditionalFormattingModifications.length
            ) {
                return getConditionalFormattingStyles(
                    ConditionalFormattingSurface.Text,
                    conditionalFormattingModifications,
                    isDarkTheme,
                    isSelected
                );
            }
            return undefined;
        }, [conditionalFormattingModifications, isDarkTheme, isSelected]);

        const densityModeString = getDensityModeString();
        const twistyComponent = (
            <MailListItemTwistyButton
                densityMode={densityModeString}
                isDarkTheme={isDarkTheme}
                isFirstLevelExpanded={isFirstLevelExpanded}
                renderMinimum={renderMinimum}
                isSecondLevelExpanded={isSecondLevelExpanded}
                isLoadingSecondLevelExpansion={isLoadingSecondLevelExpansion}
                isSelected={isSelected}
                isSingleLine={false}
                isUnread={unreadCount > 0}
                rowKey={rowKey}
                tableViewId={tableViewId}
            />
        );

        const renderFirstLineTextComponent = (text: string, tooltipText?: string) => {
            return (
                <MailListItemFirstLineText
                    highlightTerms={highlightTerms}
                    showDraftsIndicator={showDraftsIndicator}
                    isInOutboxFolder={isInOutboxFolder}
                    unreadCount={unreadCount}
                    text={text}
                    tooltipText={tooltipText}
                    isSingleLineView={false}
                    highlightAccuracy={tableView?.highlightAccuracy}
                    isScheduledSend={isScheduledSend}
                    isProactiveDraft={isProactiveDraft}
                    conditionalFormattingModifications={conditionalFormattingModifications}
                    isDarkTheme={isDarkTheme}
                    isUnread={isUnread}
                    isSelected={isSelected}
                />
            );
        };

        const renderSenderEmailLineTextComponent = (sender: SingleRecipientType) => {
            const senderText: string =
                !sender.Mailbox?.EmailAddress ||
                !isValidEmailAddress(sender.Mailbox?.EmailAddress ?? '')
                    ? `<${loc(senderEmailNotFoundLabel)}>`
                    : `<${sender.Mailbox.EmailAddress}>`;

            return (
                <MailListItemFirstLineText
                    highlightTerms={highlightTerms}
                    showDraftsIndicator={showDraftsIndicator}
                    isInOutboxFolder={isInOutboxFolder}
                    unreadCount={unreadCount}
                    text={senderText}
                    tooltipText={sender.Mailbox?.EmailAddress ?? undefined}
                    isSingleLineView={false}
                    highlightAccuracy={tableView?.highlightAccuracy}
                    isScheduledSend={isScheduledSend}
                    isProactiveDraft={isProactiveDraft}
                    isDarkTheme={isDarkTheme}
                    isUnread={isUnread}
                    isSelected={isSelected}
                />
            );
        };

        const renderCategoryWellComponent = (
            categoryWellContainerClass: string,
            categoryContainerClass: string
        ): JSX.Element => {
            return (
                <LazyCategoryWell
                    key={rowId}
                    categoryWellContainerClass={categoryWellContainerClass}
                    categoryContainerClass={categoryContainerClass}
                    categories={categories}
                    onCategoryClicked={onCategoryClicked}
                    mailboxInfo={mailboxInfo}
                    conversationId={conversationId}
                />
            );
        };

        const iconBarComponent = (
            <MailListItemIconBar
                canArchive={canArchive}
                canDelete={canDelete}
                canPin={canPin}
                effectiveMentioned={effectiveMentioned}
                hasAttachment={hasAttachment}
                hasSharepointLink={hasSharepointLink}
                importance={importance}
                isComplete={isComplete}
                isFlagged={isFlagged}
                isInArchiveFolder={isInArchiveFolder}
                isInNotesFolder={isInNotesFolder}
                isInOutboxFolder={isInOutboxFolder}
                isPinned={isPinned}
                isSingleLine={false}
                isSnoozed={isSnoozed}
                isTaggedForBigScreen={isTaggedForBigScreen}
                itemClassIcon={itemClassIcon}
                neverShowHoverIcons={!supportsHoverIcons}
                neverShowPropertyIcons={false}
                rowKey={rowKey}
                smimeType={smimeType}
                supportsFlagging={supportsFlagging}
                supportsMove={supportsMove}
                supportsPinning={supportsPinning}
                tableViewId={tableViewId}
                unreadCount={unreadCount}
                isReminderSet={isReminderSet}
                hideImportancePropertyIcon={false}
                hideFlagPropertyIcon={false}
            />
        );

        const _renderReactions = () => {
            if (!notExpanded) {
                return null;
            }

            return renderReactions({
                latestItemId,
                reactionsContainerClassName: classnames(reactionsContainer),
                rowId,
                tableViewId,
            });
        };

        const onPreviewClick = React.useCallback(
            (evt: React.MouseEvent<unknown> | React.TouchEvent<unknown>) => {
                onMailListItemClickHandler(
                    evt,
                    MailListItemSelectionSource.MailListItemRichPreview,
                    rowKey,
                    tableViewId
                );
            },
            [rowKey, tableViewId]
        );

        const attachmentPreviewWellViewState =
            getListViewStore().rowAttachmentPreviewWellViews.get(rowId);
        const previewViewState =
            attachmentPreviewWellViewState &&
            calculateAttachmentPreviewWellViewState(
                attachmentPreviewWellViewState,
                tableViewId,
                false /* isWideView */
            );

        const unreadBar = React.useMemo(() => {
            return (
                <MailListItemUnreadBar
                    isExpanded={isFirstLevelExpanded || isSecondLevelExpanded}
                    isUnread={isUnread}
                    rowKey={rowKey}
                    tableViewId={tableViewId}
                    isDarkTheme={isDarkTheme}
                    conditionalFormattingModifications={conditionalFormattingModifications}
                    cfPreviewUnreadBarVars={cfPreviewUnreadBarVars}
                />
            );
        }, [
            isFirstLevelExpanded,
            isSecondLevelExpanded,
            isUnread,
            rowKey,
            tableViewId,
            conditionalFormattingModifications,
            isDarkTheme,
            cfPreviewUnreadBarVars,
        ]);

        let firstRowStyle;
        if (isUnread) {
            firstRowStyle = firstRowThreeColumnUnreadNext;
        } else {
            firstRowStyle = firstRowThreeColumnNext;
        }

        return (
            <>
                {unreadBar}
                <div
                    className={classnames(
                        densityModeClass,
                        sclContainer,
                        shouldAllowMultiLinePreview &&
                            getPreviewTextStyles(false /*isMCL*/, numMessagePreviewLines)
                    )}
                    style={cfPreviewBackgroundColorStyle}
                    title={unifiedInboxMailboxSmtpTooltip}
                >
                    <div
                        className={classnames(
                            firstColumnThreeColumn,
                            isConversationView &&
                                !shouldShowTwisty &&
                                firstColumnThreeColumnNoTwisty,
                            containerForTwistyAndCheckboxSCL,
                            densityModeString === 'compact' && containerPaddingCompact,
                            !showCheckbox &&
                                getDensityModeCssClass(
                                    containerPaddingNoCheckboxFull,
                                    containerPaddingNoCheckboxMedium,
                                    undefined
                                ),
                            !shouldShowTwisty &&
                                showMultiSelectButton &&
                                !senderImageEnabled &&
                                !showCheckbox &&
                                multiSelectNoTwistyDisplayed
                        )}
                    >
                        {shouldShowTwisty && twistyComponent}
                        {showCheckbox && personaCheckboxComponent}
                    </div>
                    <div className={classnames(column, !showCheckbox && multiSelectNoCheckbox)}>
                        <div className={classnames(firstRowStyle, densityModeClass)}>
                            <MailListItemMarker
                                isUnauthenticatedSender={isUnauthenticatedSender}
                                shouldShowExternalLabel={shouldShowExternalLabel}
                                isSelected={isSelected}
                                bulkCategory={bulkCategory}
                            />

                            {renderFirstLineTextComponent(
                                firstLineText,
                                firstLineTooltipText ?? undefined
                            )}
                            {iconBarComponent}
                        </div>
                        {isInJunkFolder && lastSender && !isValidEmailAddress(firstLineText) ? (
                            <div>{renderSenderEmailLineTextComponent(lastSender)}</div>
                        ) : null}
                        <div
                            className={classnames(
                                isUnread
                                    ? isTypeRampUpdatesEnabled
                                        ? secondRowThreeColumnUnreadTypeRamp
                                        : secondRowThreeColumnUnread
                                    : secondRowThreeColumn,
                                !isUnread && secondRowThreeColumnRead,
                                secondRowThreeColumnPaddingNext
                            )}
                        >
                            <div
                                className={classnames(
                                    secondLineTextThreeColumn,
                                    densityModeClass,
                                    useMessageListTextStyle('Major'),
                                    conversationHeaderContent
                                )}
                            >
                                {!showPreviewText && (
                                    <CopilotInboxPriorityIcon
                                        copilotInboxScore={copilotInboxScore}
                                        scenario={isPrioritySort ? 'PrioritySort' : 'ML'}
                                        mailboxInfo={mailboxInfo}
                                        isUnread={isUnread}
                                        copilotPrioritizeOnDeliveryOn={
                                            copilotPrioritizeOnDeliveryOn
                                        }
                                    />
                                )}
                                {showCategoriesOnSubjectRow &&
                                    renderCategoryWellComponent(
                                        categoryWellThreeColumn,
                                        categoryContainerThreeColumn
                                    )}
                                <MailListItemLineText
                                    addOverflowStyles={true}
                                    highlightTerms={highlightTerms}
                                    highlightAccuracy={tableView?.highlightAccuracy}
                                    text={secondLineText}
                                    tooltipText={secondLineTooltipText ?? undefined}
                                    conditionalFormattingModifications={
                                        conditionalFormattingModifications
                                    }
                                    isDarkTheme={isDarkTheme}
                                    isSelected={isSelected}
                                />
                            </div>
                            <MailListItemUnreadCount
                                conditionalFormattingModifications={
                                    conditionalFormattingModifications
                                }
                                unreadCount={unreadCount}
                                isDarkTheme={isDarkTheme}
                            />
                            {showReactionsOnSubjectRow && _renderReactions()}
                            {isTaggedForBigScreen ? (
                                /* eslint-disable-next-line react/jsx-no-useless-fragment -- (https://aka.ms/OWALintWiki)
                                 * Don't use react fragments if there are no children
                                 *	> Fragments should contain more than one child - otherwise, there’s no need for a Fragment at all. */
                                <></> // TODO: Update this after get the proper message to display.
                            ) : returnTime ? (
                                <MailListItemTimestamp
                                    isPinned={isPinned}
                                    isSingleLine={false}
                                    isSnoozed={true}
                                    returnTime={returnTime}
                                    supportsPinning={supportsPinning}
                                    isCfPreview={isCfPreview}
                                    conditionalFormattingModifications={
                                        conditionalFormattingModifications
                                    }
                                    isDarkTheme={isDarkTheme}
                                    isSelected={isSelected}
                                />
                            ) : (
                                !isFirstLevelExpanded &&
                                !isSecondLevelExpanded && (
                                    <MailListItemTimestamp
                                        isPinned={isPinned}
                                        isSingleLine={false}
                                        isUnread={isUnread}
                                        lastDeliveryTimestamp={lastDeliveryTimestamp}
                                        returnTime={returnTime ?? ''}
                                        supportsPinning={supportsPinning}
                                        isCfPreview={isCfPreview}
                                        conditionalFormattingModifications={
                                            conditionalFormattingModifications
                                        }
                                        isDarkTheme={isDarkTheme}
                                        isSelected={isSelected}
                                    />
                                )
                            )}
                        </div>
                        {showPreviewTextConditionally && (
                            <div className={classnames(previewDisplaySCL, densityModeClass)}>
                                <div
                                    className={classnames(
                                        previewDisplaySCLCategories,
                                        useTwoLinePreviewFlow && previewDisplaySCLCategoriesTwoLine
                                    )}
                                >
                                    {showPreviewText && (
                                        <span
                                            className={
                                                shouldAllowMultiLinePreview &&
                                                numMessagePreviewLines === 2
                                                    ? priorityIconAlignTop
                                                    : undefined
                                            }
                                        >
                                            <CopilotInboxPriorityIcon
                                                copilotInboxScore={copilotInboxScore}
                                                scenario={isPrioritySort ? 'PrioritySort' : 'ML'}
                                                mailboxInfo={mailboxInfo}
                                                isUnread={isUnread}
                                                copilotPrioritizeOnDeliveryOn={
                                                    copilotPrioritizeOnDeliveryOn
                                                }
                                            />
                                        </span>
                                    )}
                                    {showCategoriesOnPreviewRow &&
                                        renderCategoryWellComponent(
                                            classnames(
                                                categoryWellThreeColumn,
                                                useTwoLinePreviewFlow &&
                                                    previewDisplaySCLCategoryWellFloat
                                            ),
                                            getDensityModeCssClass(
                                                categoryContainerThreeColumnFull,
                                                categoryContainerThreeColumnMediumOrCompact,
                                                categoryContainerThreeColumnMediumOrCompact
                                            )
                                        )}
                                    {showPreviews && (
                                        <MailListItemPreviewDisplay
                                            highlightTerms={highlightTerms}
                                            highlightAccuracy={tableView?.highlightAccuracy}
                                            previewDisplay={thirdLineText}
                                            rowId={rowId}
                                            mailboxInfo={mailboxInfo}
                                            copilotPrioritizeOnDeliveryOn={
                                                copilotPrioritizeOnDeliveryOn
                                            }
                                            cfTextStyle={conditionalFormattingTextStyle}
                                            isSelected={isSelected}
                                            numMessagePreviewLines={numMessagePreviewLines}
                                            useTextFlowLayout={useTwoLinePreviewFlow}
                                        />
                                    )}
                                </div>
                                {showFolderTag && (
                                    <div className={threeColFolderTag}>
                                        <FolderTag
                                            folderId={parentFolderId}
                                            parentFolderDisplayName={parentFolderDisplayName}
                                            isPreviewTextOn={showPreviewText}
                                            isSingleLine={false}
                                            mailboxGuids={mailboxGuids}
                                            shouldBeInTabOrder={isSelected}
                                        />
                                    </div>
                                )}
                                {_renderReactions()}
                            </div>
                        )}
                        {showLabelsContainer && (
                            <MailListItemLabelsContainer
                                categories={categories}
                                categoryContainerClass={categoryContainerThreeColumn}
                                categoryWellContainerClass={categoryWellThreeColumn}
                                isSingleLine={false}
                                mailboxGuids={mailboxGuids}
                                parentFolderDisplayName={parentFolderDisplayName}
                                parentFolderId={parentFolderId}
                                rowId={rowId}
                                showCategories={showCategories && notExpanded}
                                showFolderTag={showFolderTag}
                                showPreviewText={showPreviewText}
                                mailboxInfo={mailboxInfo}
                            />
                        )}
                        {showPreviews && showAttachmentPreview && previewViewState && (
                            <MailListItemRichPreviewWell
                                isWideView={false}
                                onPreviewClick={onPreviewClick}
                                tableViewId={tableViewId}
                                previewViewState={previewViewState}
                                attachmentPreviewWellViewState={attachmentPreviewWellViewState}
                            />
                        )}
                        {shouldShowRSVP && (
                            <MailListItemMeetingInfo
                                conversationId={conversationId}
                                isSelected={isSelected}
                                isSingleLineView={false}
                                latestItemId={latestItemId}
                                listViewType={listViewType}
                                rowKey={rowKey}
                            />
                        )}
                        {isNeedsReplyView(mailTableQuery) && (
                            <NeedsReplyItemFeedback
                                rowKey={rowKey}
                                tableViewId={tableViewId}
                                mailboxInfo={mailboxInfo}
                            />
                        )}
                        {shouldSuggestUnsubscribe && (
                            <SuggestedUnsubscribe
                                isSingleLineView={false}
                                isSelected={isSelected}
                                rowId={rowId}
                                tableView={tableView}
                            />
                        )}
                    </div>
                </div>
            </>
        );
    },
    'MailListItemFullThreeColumn'
);
export default defaultExport;
